import { Component, computed, EventEmitter, input, output, inject } from '@angular/core';

import Highcharts from 'highcharts/esm/highstock';

import { HighchartsChartDirective, ChartConstructorType } from 'highcharts-angular';

import { TranslateService } from '@ngx-translate/core';
import { UtilityService } from '../../utils/utility.service';
import { LocaleService } from '../../services/locale/locale.service';
import { ChartEvent } from './chart.event';

@Component({
  selector: 'td-wb-chart',
  templateUrl: 'chart.component.html',
  styleUrls: ['chart.component.scss'],
  imports: [HighchartsChartDirective]
})
export class ChartComponent {
  private readonly localeService = inject(LocaleService);
  private readonly translateService = inject(TranslateService);

  readonly type = input<ChartConstructorType>('chart');
  readonly options = input<Highcharts.Options>({});

  readonly create: ReturnType<typeof output<Highcharts.Chart>> = output<Highcharts.Chart>();
  readonly click: ReturnType<typeof output<ChartEvent>> = output<ChartEvent>();
  readonly addSeries: ReturnType<typeof output<ChartEvent>> = output<ChartEvent>();
  readonly afterPrint: ReturnType<typeof output<ChartEvent>> = output<ChartEvent>();
  readonly beforePrint: ReturnType<typeof output<ChartEvent>> = output<ChartEvent>();
  readonly drilldown: ReturnType<typeof output<ChartEvent>> = output<ChartEvent>();
  readonly drillup: ReturnType<typeof output<ChartEvent>> = output<ChartEvent>();
  readonly load: ReturnType<typeof output<Highcharts.Chart>> = output<Highcharts.Chart>();
  readonly redraw: ReturnType<typeof output<ChartEvent>> = output<ChartEvent>();
  readonly selection: ReturnType<typeof output<ChartEvent>> = output<ChartEvent>();

  chart: Highcharts.Chart | undefined;

  readonly chartOptions: ReturnType<typeof computed<Highcharts.Options>> = computed(() => {
    return this.mergeWithDefaultOptions(this.options());
  });

  onChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
    this.create.emit(chart);
    this.load.emit(chart);
  }

  /**
   * Merges the provided options with default options
   * @param userOptions The user-provided options
   * @returns The merged options
   */
  private mergeWithDefaultOptions(userOptions: Highcharts.Options): Highcharts.Options {
    const defaultOptions = this.getDefaultOptions();

    // Setup events
    this.setupEvents(defaultOptions);

    // Adjust axis type to user one
    if (userOptions.xAxis && Array.isArray(userOptions.xAxis)) {
      defaultOptions.xAxis = [defaultOptions.xAxis] as Highcharts.XAxisOptions;
    }
    if (userOptions.yAxis && Array.isArray(userOptions.yAxis)) {
      defaultOptions.yAxis = [defaultOptions.yAxis] as Highcharts.YAxisOptions;
    }

    // Ensure chart options have chart object
    if (!userOptions.chart) {
      userOptions.chart = {};
    }

    // Merge options
    return UtilityService.deepExtend({}, defaultOptions, userOptions);
  }

  /**
   * Returns the default options for the chart
   * @returns The default options
   */
  private getDefaultOptions(): Highcharts.Options {
    const localeDetails = this.localeService.getLocaleDetails();
    return {
      accessibility: {
        enabled: true
      },
      chart: {
        zooming: {
          mouseWheel: false
        },
        events: {},
        style: {
          fontFamily: 'Roboto, Helvetica, sans-serif'
        },
        backgroundColor: 'transparent',
        borderWidth: 0,
        borderRadius: 0,
        spacing: [10, 10, 15, 10]
      },
      lang: {
        rangeSelectorZoom: '',
        noData: this.translateService.instant('tdWb.chartService.NO_CHART_DATA'),
        decimalPoint: localeDetails.decimalSeparator,
        thousandsSep: localeDetails.groupSeparator,
        months: localeDetails.calendarStrings.months,
        weekdays: localeDetails.calendarStrings.weekdays,
        shortMonths: localeDetails.calendarStrings.shortMonths
      },
      noData: {
        style: {
          fontFamily: 'Roboto, Helvetica, sans-serif',
          fontWeight: 'bold',
          fontSize: '16px',
          color: '#666666'
        }
      },
      title: {
        style: {
          color: '#333333',
          fontSize: '18px',
          fontWeight: 'bold'
        }
      },
      subtitle: {
        style: {
          color: '#666666',
          fontSize: '14px'
        }
      },
      legend: {
        enabled: false
      },
      tooltip: {
        outside: true,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderWidth: 1,
        borderRadius: 3,
        shadow: true,
        style: {
          color: '#333333',
          fontSize: '12px'
        }
      },
      plotOptions: {
        series: {
          animation: false,
          events: {},
          point: {
            events: {}
          }
        }
      },
      xAxis: {
        events: {},
        labels: {
          style: {
            color: '#666666',
            fontSize: '11px'
          }
        },
        lineColor: '#cccccc',
        tickColor: '#cccccc'
      },
      yAxis: {
        events: {},
        labels: {
          style: {
            color: '#666666',
            fontSize: '11px'
          }
        },
        lineColor: '#cccccc',
        tickColor: '#cccccc',
        gridLineColor: '#e6e6e6'
      },
      credits: {
        enabled: false
      },
      scrollbar: { enabled: false },
      exporting: {
        buttons: {
          contextButton: {
            enabled: false
          }
        }
      }
    };
  }

  /**
   * Sets up event handlers for the chart
   * @param options The options object to add event handlers to
   */
  private setupEvents(options: Highcharts.Options): void {
    [
      'click',
      'addSeries',
      'afterPrint',
      'beforePrint',
      'drilldown',
      'drillup',
      'redraw',
      'selection'
    ].forEach((eventName: string): void => {
      const inlinedEvent = (event: Event): void => {
        const outputEmitter = this[eventName as keyof ChartComponent] as unknown as EventEmitter<unknown>;
        if (outputEmitter instanceof EventEmitter) {
          outputEmitter.emit(new ChartEvent(event, this));
        }
      };

      if (options.chart?.events) {
        options.chart.events[eventName] = inlinedEvent;
      }
    });
  }
}
